import React, { useState, useEffect } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  createFilterOptions,
  Box,
  Chip,
  CircularProgress,
} from '@mui/material';
import { FieldDefinition, Operator, SelectOption } from '../types';

// Operators that support being expanded into multiple expressions
const SUPPORTS_MULTI_VALUE = ['equals', 'not_equals'];

interface ValueInputProps {
  field: FieldDefinition;
  operator: Operator;
  value: any[];
  onChange: (value: any[]) => void;
  fetchResourceValues: (resourceId: string, searchTerm?: string) => Promise<SelectOption[]>;
  locale: { [key: string]: string };
}

interface FreeSoloOption {
  label: string;
  value: string | number;
}

const filter = createFilterOptions<FreeSoloOption>();

export const ValueInput: React.FC<ValueInputProps> = ({
  field,
  operator,
  value,
  onChange,
  fetchResourceValues,
  locale,
}) => {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Load options for select/multiselect fields
  useEffect(() => {
    if ((field.fieldType === 'select' || field.fieldType === 'multiselect') && field.resolveToResourceId) {
      setLoading(true);
      fetchResourceValues(field.resolveToResourceId, searchTerm)
        .then(setOptions)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [field.fieldType, field.resolveToResourceId, searchTerm, fetchResourceValues]);

  // For 'present' and 'not_present' operators, no input is needed
  if (operator === 'present' || operator === 'not_present') {
    return null;
  }

  // Handle different field types
  switch (field.fieldType) {
    case 'text':
    case 'numeric':
    case 'currency':
      // For operators like 'equals', allow multiple comma-separated values.
      if (SUPPORTS_MULTI_VALUE.includes(operator)) {
        return (
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={value || []}
            onChange={(_, newValue) => onChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                type={field.fieldType === 'text' || field.fieldType === 'numeric' || field.fieldType === 'currency' ? 'number' : 'text'}
                placeholder="Enter one or more values..."
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                />
              ))
            }
            size="small"
          />
        )
      }

      if (operator === 'between') {
        return (
          <Box sx={{
            display: 'grid',
            gap: 1,
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            width: '100%'
          }}>
            <TextField
              type="number"
              value={value[0] || ''}
              onChange={(e) => onChange([e.target.value, value[1]])}
              placeholder={locale.betweenFromPlaceholder}
              size="small"
              sx={{ 
                flex: 1,
                minWidth: { xs: '100%', sm: '120px' },
                '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 },
                '& .MuiInputLabel-root': { fontSize: '0.875rem' }
              }}
            />
            <Box sx={{ 
              display: { xs: 'none', sm: 'flex' }, 
              alignItems: 'center', 
              fontSize: '0.875rem',
              color: 'text.secondary',
              px: 0.5
            }}>-</Box>
            <TextField
              type="number"
              value={value[1] || ''}
              onChange={(e) => onChange([value[0], e.target.value])}
              placeholder={locale.betweenToPlaceholder}
              size="small"
              sx={{ 
                flex: 1,
                minWidth: { xs: '100%', sm: '120px' },
                '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 },
                '& .MuiInputLabel-root': { fontSize: '0.875rem' }
              }}
            />
          </Box>
        );
      }
      return (
        <TextField
          fullWidth
          type="number"
          value={value[0] || ''}
          onChange={(e) => onChange([e.target.value])}
          placeholder={locale.valuePlaceholder}
          size="small"
          InputProps={field.fieldType === 'currency' ? { 
            startAdornment: <span style={{ fontSize: '0.875rem' }}>R$</span> 
          } : undefined}
          sx={{ 
            '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 },
            '& .MuiInputLabel-root': { fontSize: '0.875rem' }
          }}
        />
      );

    case 'date':
      if (operator === 'between') {
        return (
          <Box sx={{
            display: 'grid',
            gap: 1,
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            width: '100%'
          }}>
            <TextField
              type="date"
              value={value[0] || ''}
              onChange={(e) => onChange([e.target.value, value[1]])}
              placeholder={locale.betweenFromPlaceholder}
              size="small"
              sx={{ 
                flex: 1,
                '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 },
                '& .MuiInputLabel-root': { fontSize: '0.875rem' }
              }}
              InputLabelProps={{ shrink: true }}
            />
            <span style={{ fontSize: '0.875rem' }}>-</span>
            <TextField
              type="date"
              value={value[1] || ''}
              onChange={(e) => onChange([value[0], e.target.value])}
              placeholder={locale.betweenToPlaceholder}
              size="small"
              sx={{ 
                flex: 1,
                '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 },
                '& .MuiInputLabel-root': { fontSize: '0.875rem' }
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        );
      }
      return (
        <TextField
          fullWidth
          type="date"
          value={value[0] || ''}
          onChange={(e) => onChange([e.target.value])}
          placeholder={locale.valuePlaceholder}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ 
            '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 },
            '& .MuiInputLabel-root': { fontSize: '0.875rem' }
          }}
        />
      );

    case 'boolean':
      return (
        <FormControl fullWidth size="small">
          <Select
            value={value[0] || ''}
            onChange={(e) => onChange([e.target.value])}
            displayEmpty
            sx={{ fontSize: '0.875rem' }}
          >
            <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
              <em>{locale.valuePlaceholder}</em>
            </MenuItem>
            <MenuItem value="true" sx={{ fontSize: '0.875rem' }}>{locale.trueBooleanLabel}</MenuItem>
            <MenuItem value="false" sx={{ fontSize: '0.875rem' }}>{locale.falseBooleanLabel}</MenuItem>
          </Select>
        </FormControl>
      );

    case 'select':
      // If a resourceId is provided and we have options, show the dropdown.
      if (field.resolveToResourceId && options.length > 0) {
        return (
          <Autocomplete
            fullWidth
            options={options}
            getOptionLabel={(option) => option.label || ''}
            value={options.find(o => o.value === value[0]) || null}
            onChange={(_, newValue) => onChange(newValue ? [newValue.value] : [])}
            onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
            loading={loading}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={locale.searchPlaceholder}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        );
      }
      // Otherwise, fall back to a simple text field for manual input.
      return (
        <TextField
          fullWidth
          value={value[0] || ''}
          onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
          placeholder={locale.valuePlaceholder}
          size="small"
        />
      );

    case 'multiselect':
      // If a resourceId is provided and we have options, show the multi-select dropdown.
      if (field.resolveToResourceId && options.length > 0) {
        return (
          <FormControl fullWidth size="small">
            <Autocomplete
              multiple
              options={options}
              getOptionLabel={(option) => option.label || ''}
              value={options.filter(o => (value || []).includes(o.value))}
              onChange={(_, newValues) => onChange(newValues.map(v => v.value))}
              onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
              loading={loading}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={locale.searchPlaceholder}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={option.label}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </FormControl>
        );
      }
      // Otherwise, fall back to a simple text field for comma-separated manual input.
      return (
        <TextField
          fullWidth
          value={(value || []).join(', ')}
          onChange={(e) => {
            const entries = e.target.value ? e.target.value.split(',').map(v => v.trim()).filter(Boolean) : [];
            onChange(entries);
          }}
          placeholder="Enter values, comma-separated"
          size="small"
        />
      );
      
    default:
      return (
        <TextField
          fullWidth
          value={value[0] || ''}
          onChange={(e) => onChange([e.target.value])}
          placeholder={locale.valuePlaceholder}
          size="small"
        />
      );
  }
};